// client/src/pages/Game/Battlefield/components/RpsScreen.jsx

import React, { useState, useEffect, useRef } from 'react';
import soundManager from '../../../../utils/soundManager';
import './RpsScreen.css';

const CHOICES = [
  { id: 'rock', label: 'Pierre', emoji: '✊' },
  { id: 'paper', label: 'Feuille', emoji: '✋' },
  { id: 'scissors', label: 'Ciseaux', emoji: '✌️' },
];

const EMOJI = { rock: '✊', paper: '✋', scissors: '✌️' };

/**
 * Écran de shi-fu-mi (pierre-feuille-ciseaux) affiché AVANT le mulligan.
 * Le gagnant commence la partie.
 *
 * États gérés :
 *  - choosing : mains qui se secouent, 3 boutons actifs
 *  - waiting  : choix joueur fait, attente de l'adversaire
 *  - reveal   : les deux choix sont révélés + bannière de résultat
 *  - tie      : égalité → petit flash puis on rejoue
 */
function RpsScreen({ gameState, onChoose }) {
  const rps = gameState?.rps || {};
  const myChoice = rps.playerChoice || null;
  const oppChoice = rps.opponentChoice || null;
  const result = rps.result || null;

  // Flash d'égalité : les choix sont réinitialisés côté moteur, on les
  // récupère depuis lastAction (payload rps) le temps de l'animation.
  // ⚠️ Le timer NE DOIT PAS être annulé par les re-rendus : l'état est
  // re-cloné à chaque broadcast → l'identité de lastAction change → un
  // cleanup classique annulerait le clearTimeout du flash (bug d'écran
  // gelé sur « Égalité ! On rejoue. »). Garde par signature + timer en ref.
  const [tieFlash, setTieFlash] = useState(null);
  const lastTieRef = useRef(null);
  const tieTimerRef = useRef(null);

  useEffect(() => {
    const la = gameState?.lastAction;
    if (la && la.type === 'RPS_TIE' && la.rps) {
      const sig = `${la.rps.ties}-${la.rps.playerChoice}-${la.rps.opponentChoice}`;
      if (lastTieRef.current !== sig) {
        lastTieRef.current = sig;
        setTieFlash(la.rps);
        if (tieTimerRef.current) clearTimeout(tieTimerRef.current);
        tieTimerRef.current = setTimeout(() => setTieFlash(null), 1700);
      }
    }
    // volontairement PAS de cleanup ici (voir note ci-dessus)
  }, [gameState?.lastAction]);

  // Nettoyage au démontage uniquement
  useEffect(() => () => {
    if (tieTimerRef.current) clearTimeout(tieTimerRef.current);
  }, []);

  const revealed = result || tieFlash;
  const myReveal = revealed
    ? (result ? result.playerChoice : tieFlash.playerChoice)
    : null;
  const oppReveal = revealed
    ? (result ? result.opponentChoice : tieFlash.opponentChoice)
    : null;

  const iWon = result && result.winner === 'player';

  const handlePick = (id) => {
    if (myChoice || revealed) return; // déjà joué / animation en cours
    soundManager.play('cardFlip');
    onChoose(id);
  };

  return (
    <div className="rps-overlay">
      <div className="rps-panel">
        <div className="rps-title">QUI COMMENCE ?</div>
        <div className="rps-subtitle">Pierre · Feuille · Ciseaux</div>

        <div className="rps-arena">
          <div className="rps-side">
            <div
              className={`rps-hand ${revealed ? 'reveal' : (myChoice ? 'shake-fast' : 'shake')}`}
            >
              {myReveal ? EMOJI[myReveal] : '✊'}
            </div>
            <div className="rps-label">Vous</div>
          </div>

          <div className="rps-vs">VS</div>

          <div className="rps-side">
            <div
              className={`rps-hand opponent ${revealed ? 'reveal' : (myChoice ? 'shake-fast' : 'shake')}`}
            >
              {oppReveal ? EMOJI[oppReveal] : '✊'}
            </div>
            <div className="rps-label">Adversaire</div>
          </div>
        </div>

        {/* Bannière de résultat */}
        {result && (
          <div className={`rps-banner ${iWon ? 'win' : 'lose'}`}>
            {iWon ? '🏆 Vous gagnez — vous commencez !' : '💨 L\'adversaire commence...'}
          </div>
        )}
        {tieFlash && !result && (
          <div className="rps-banner tie">🤝 Égalité ! On rejoue.</div>
        )}

        {/* Choix */}
        {!revealed && (
          <div className="rps-choices">
            {CHOICES.map(c => (
              <button
                key={c.id}
                type="button"
                className={`rps-choice-btn ${myChoice === c.id ? 'picked' : ''}`}
                disabled={!!myChoice}
                onClick={() => handlePick(c.id)}
              >
                <span className="rps-choice-emoji">{c.emoji}</span>
                <span className="rps-choice-label">{c.label}</span>
              </button>
            ))}
          </div>
        )}

        {/* Attente adversaire */}
        {myChoice && !revealed && (
          <div className="rps-waiting">L'adversaire choisit…</div>
        )}
      </div>
    </div>
  );
}

export default RpsScreen;
